import { useStore } from '@nanostores/react';
import { Button, HStack, Text } from '@astryxdesign/core';
import { Link as RouterLink, NavLink, useNavigate } from 'react-router';

import { $auth } from '../stores/auth';
import { logout } from '../stores/auth';

function Brand() {
  return (
    <RouterLink to="/" className="brand">
      <span className="brand__mark" aria-hidden="true">
        {/* Observed series (solid) turning into a forecast (dashed) — a "V" made of a chart. */}
        <svg viewBox="0 0 32 32" width="17" height="17" fill="none">
          <g
            stroke="currentColor"
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5.5 7.5 L16 25.5" />
            <path d="M16 25.5 L26.4 7.5" strokeDasharray="4.4 3.2" />
          </g>
        </svg>
      </span>
      <span>VecTime Cloud</span>
    </RouterLink>
  );
}

/** Marketing-site header: nav links plus sign-in / dashboard depending on state. */
export function SiteHeader() {
  const auth = useStore($auth);

  return (
    <header className="nav">
      <div className="shell nav__inner">
        <Brand />

        <nav className="nav__links nav__links--marketing" aria-label="Main">
          <NavLink to="/pricing" className="nav__link">
            Pricing
          </NavLink>
          <NavLink to="/docs" className="nav__link">
            API docs
          </NavLink>
          <NavLink to="/integrations/n8n" className="nav__link">
            n8n
          </NavLink>
          <NavLink to="/blog" className="nav__link">
            Blog
          </NavLink>
        </nav>

        <HStack gap={1.5} vAlign="center" wrap="wrap">
          {auth.user ? (
            <>
              {/* Decorative on a phone: a long email would push the nav wider
                  than the viewport, so it is hidden at narrow widths. */}
              <span className="nav__identity">
                <Text size="sm" color="secondary">
                  {auth.user.email}
                </Text>
              </span>
              <Button label="Dashboard" variant="primary" size="sm" href="/app" />
            </>
          ) : (
            <>
              <Button label="Sign in" variant="ghost" size="sm" href="/login" />
              <Button label="Start free" variant="primary" size="sm" href="/register" />
            </>
          )}
        </HStack>
      </div>
    </header>
  );
}

/** Dashboard header: identity + sign out, no marketing nav. */
export function AppHeader() {
  const auth = useStore($auth);
  const navigate = useNavigate();

  return (
    <header className="nav">
      <div className="shell nav__inner">
        <Brand />
        <HStack gap={2} vAlign="center" wrap="wrap">
          <span className="nav__identity">
            <Text size="sm" color="secondary">
              {auth.plan ? `${auth.plan.name} plan` : ''}
            </Text>
          </span>
          <Button
            label="Sign out"
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
              void navigate('/');
            }}
          />
        </HStack>
      </div>
    </header>
  );
}

const FOOTER_COLUMNS: Array<{ heading: string; links: Array<{ to: string; label: string }> }> = [
  {
    heading: 'Product',
    links: [
      { to: '/pricing', label: 'Pricing' },
      { to: '/docs', label: 'API reference' },
      { to: '/app/playground', label: 'Playground' },
      { to: '/integrations/n8n', label: 'n8n integration' },
    ],
  },
  {
    heading: 'Learn',
    links: [
      { to: '/blog', label: 'Blog' },
      { to: '/blog/timesfm-vs-prophet-benchmark', label: 'TimesFM vs Prophet' },
      { to: '/blog/quantile-forecasting-explained', label: 'Reading quantiles' },
      { to: '/blog/forecast-api-rate-limits', label: 'Rate limits & quota' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { to: '/privacy', label: 'Privacy' },
      { to: '/terms', label: 'Terms' },
      { to: '/register', label: 'Create account' },
      { to: '/login', label: 'Sign in' },
    ],
  },
];

export function SiteFooter() {
  return (
    <footer className="footer">
      <div className="shell">
        <div className="footer__grid">
          <div className="footer__col">
            <Brand />
            <Text size="sm" color="secondary">
              <span className="measure--wide">
                Time-series forecasting as an HTTP call. Google&apos;s TimesFM 2.5 open weights behind
                a metered API — zero-shot, probabilistic, batched.
              </span>
            </Text>
          </div>
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.heading} className="footer__col">
              <span className="footer__heading">{col.heading}</span>
              {col.links.map((l) => (
                <NavLink key={l.to} to={l.to} className="footer__link">
                  {l.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        <div className="footer__bottom">
          <Text size="sm" color="secondary">
            © {new Date().getFullYear()} VecTime Cloud. Model weights © Google, Apache 2.0.
          </Text>
          <Text size="sm" color="secondary">
            team@vectime.cloud
          </Text>
        </div>
      </div>
    </footer>
  );
}
